import { GenericDropdownOption } from 'App.types';
import { creatorAgency, creatorAgencyQueries } from '@round/api';
import { useInfiniteQuery } from '@tanstack/react-query';
import useSelectProps, { SelectProps } from 'Hooks/useSelectProps';
import { ValueType } from 'react-select';

export type AgencyPayeeOption = GenericDropdownOption<number> & {
    image: string | null;
};

export const mapAgencyPayeeToOption = (payee: creatorAgency.CreatorAgencyPayee): AgencyPayeeOption => ({
    value: payee.id,
    label: payee.name,
    image: payee.image,
});

type Params = {
    initialValue?: ValueType<AgencyPayeeOption, false>;
};

export default function useAgencyPayeeSelect({ initialValue }: Params = {}) {
    const { debouncedSearchValue, getProps, setValue } = useSelectProps<AgencyPayeeOption, false>({ initialValue });

    const { data, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery(
        creatorAgencyQueries.payee.options.infinite({ search: debouncedSearchValue, page_size: 50 })
    );
    const options: AgencyPayeeOption[] = data?.pages.flatMap((page) => page.results.map(mapAgencyPayeeToOption)) ?? [];

    return {
        getProps: (override?: Partial<SelectProps<AgencyPayeeOption, false>>) =>
            getProps({
                options,
                noOptionsMessage: () => 'No agencies found',
                isLoading: isFetching,
                onMenuScrollToBottom: () => {
                    if (hasNextPage && !isFetching) {
                        fetchNextPage();
                    }
                },
                filterOption: null,
                ...override,
            }),
        setValue,
    };
}
